import { ArrowDown, ArrowUp, Minus } from "@phosphor-icons/react";
import { Text } from "@radix-ui/themes";

import { MetricBlockCondensed } from "./MetricBlockCondensed";

import {
  agentComponentIdAttr,
  type ChatAtomAgentComponentId,
  type ChatAtomAgentComponentInstanceId,
  CHAT_ATOM_AGENT_COMPONENT_IDS,
} from "../../../agentComponents/chatAtomRegistry";

type TrendInput =
  | "up-positive"
  | "up-negative"
  | "down-positive"
  | "down-negative"
  | "neutral"
  | "up"
  | "down";

type CanonicalTrend =
  | "up-positive"
  | "up-negative"
  | "down-positive"
  | "down-negative"
  | "neutral";

interface MetricBlockProps {
  label: string;
  value: string;
  trend?: TrendInput;
  /**
   * Greeting-stat density. Compact renders MetricBlockCondensed (Figma 49612:38)
   * and never shows a trend.
   */
  size?: "default" | "compact";
  /** Defaults to the base metric-block id; pass a suffixed id when N tiles share a surface. */
  agentComponentId?:
    | ChatAtomAgentComponentId
    | ChatAtomAgentComponentInstanceId;
  "data-testid"?: string;
}

const normalizeTrend = (trend: TrendInput): CanonicalTrend => {
  if (trend === "up") return "up-positive";
  if (trend === "down") return "down-negative";
  return trend;
};

const TREND_CONFIG: Record<
  CanonicalTrend,
  {
    direction: "up" | "down" | "neutral";
    iconColorClass: string;
    barColorClass: string;
    ariaLabel: string;
  }
> = {
  "up-positive": {
    direction: "up",
    iconColorClass: "text-malachite-700",
    barColorClass: "bg-malachite-600",
    ariaLabel: "Trend: up, positive outcome",
  },
  "up-negative": {
    direction: "up",
    iconColorClass: "text-puntt-red-9",
    barColorClass: "bg-puntt-red-9",
    ariaLabel: "Trend: up, negative outcome",
  },
  "down-positive": {
    direction: "down",
    iconColorClass: "text-malachite-700",
    barColorClass: "bg-malachite-600",
    ariaLabel: "Trend: down, positive outcome",
  },
  "down-negative": {
    direction: "down",
    iconColorClass: "text-puntt-red-9",
    barColorClass: "bg-puntt-red-9",
    ariaLabel: "Trend: down, negative outcome",
  },
  neutral: {
    direction: "neutral",
    iconColorClass: "text-puntt-neutral-gray-9",
    barColorClass: "bg-puntt-neutral-gray-7",
    ariaLabel: "Trend: neutral",
  },
};

const TrendIcon = ({ variant }: { variant: CanonicalTrend }) => {
  const config = TREND_CONFIG[variant];
  const Icon =
    config.direction === "up"
      ? ArrowUp
      : config.direction === "down"
        ? ArrowDown
        : Minus;
  return (
    <span
      role="img"
      aria-label={config.ariaLabel}
      data-testid={`metric-block-trend-icon-${variant}`}
    >
      <Icon size={14} className={config.iconColorClass} />
    </span>
  );
};

export function MetricBlock({
  label,
  value,
  trend,
  size = "default",
  agentComponentId = CHAT_ATOM_AGENT_COMPONENT_IDS.metricBlock,
  "data-testid": dataTestId = "metric-block",
}: MetricBlockProps) {
  // why: Figma MetricBlockCondensed (49612:38) is the compact greeting-stat chip.
  if (size === "compact") {
    return (
      <MetricBlockCondensed
        label={label}
        value={value}
        agentComponentId={agentComponentId}
        data-testid={dataTestId}
      />
    );
  }

  const variant = !trend ? undefined : normalizeTrend(trend);
  return (
    <div
      {...agentComponentIdAttr(agentComponentId)}
      data-testid={dataTestId}
      data-size={size}
      className="rounded-xl border border-puntt-neutral-gray-6 bg-base-white p-4 shadow-sm transition-all duration-200 hover:border-puntt-accent-7 hover:shadow-md"
    >
      {variant && (
        <div
          aria-hidden="true"
          data-testid={`metric-block-trend-bar-${variant}`}
          className={`mb-2 h-0.5 w-8 rounded-full ${TREND_CONFIG[variant].barColorClass}`}
        />
      )}
      <Text as="p" size="1" className="text-puntt-neutral-gray-9">
        {label}
      </Text>
      <div className="mt-1 flex items-center gap-1.5">
        <Text size="6" weight="bold" className="text-puntt-neutral-gray-12">
          {value}
        </Text>
        {variant && <TrendIcon variant={variant} />}
      </div>
    </div>
  );
}

export { MetricBlockCondensed } from "./MetricBlockCondensed";
